import { createStore } from 'zustand/vanilla';
import { subscribeWithSelector, devtools } from 'zustand/middleware';
import { _emitExposure, type ExposureEvent } from './exposure-signal';
import type { FlagProviderAdapter } from '../providers/provider.types';
import type { FlagContext } from '../types';
import { userUnitExperimentKeys } from '../registry/experiment-unit';

/**
 * Structured exposure identity. Callers pass kind + key; the STORE derives
 * its internal namespaced dedup string (`exp:${key}` / `gate:${key}`) from
 * it — the namespace convention never leaves this module.
 */
export type FlagExposure = Pick<ExposureEvent, 'kind' | 'key'>;

export type FlagStoreState = {
  values: Record<string, unknown>;
  isReady: boolean;
  exposed: Set<string>;
  /** MV-0-017 — key → userID (= customerId) the key's value was resolved for
   *  on the Statsig client (user-unit experiments only). Absent = the value is
   *  still the server's stableID-only evaluation, NOT the customer's arm. */
  userResolved: Record<string, string>;
  /** MV-0-017 D5 — key → userIDs whose bounded resolution elapsed
   *  unresolved (the customer's Statsig update settled — success, SDK timeout
   *  or error — without the key resolving). Sticky: a later resolution for the
   *  same (key, userID) is still recorded in `userResolved`/`values`, but
   *  `userResolutionState` keeps reporting `'timed-out'` so a customer
   *  released to control never flips. */
  userTimedOut: Record<string, string[]>;
  /** MV-0-017 — key → userIDs the key EVER resolved for this page view. Not
   *  forgotten by `clearUserResolution`: a pair a customer was shown never
   *  times out later. */
  userEverResolved: Record<string, string[]>;
  /** MV-0-017 — the customer the Statsig client user must carry (set by
   *  `identifyCustomer`, providers/statsig/client-instance.ts). Kept here so a
   *  request made before any client is published is applied when one is.
   *  Browser only. */
  statsigCustomer: StatsigUnitRequest | null;
  /** MVX-371 U6 — the person the Statsig client user must carry as
   *  `customIDs.personID` (set by `identifyPerson`); same rules as
   *  `statsigCustomer`. */
  statsigPerson: StatsigUnitRequest | null;
  /** User-unit keys released not enrolled for this page view (no id, no
   *  client, or timed out). Sticky until the next identity reset. */
  released: Record<string, true>;
  hydrate: (bootstrap: Record<string, unknown>) => void;
  evaluate: (provider: FlagProviderAdapter, ctx: FlagContext) => Promise<void>;
  getValue: <T>(key: string, defaultValue?: T) => T | undefined;
  markExposed: (exposure: FlagExposure) => boolean;
  unmarkExposed: (exposure: FlagExposure) => void;
  /** Write a user-unit experiment's client-evaluated value for `userId`. An
   *  empty value (null / `{}` — not enrolled) marks the key resolved but
   *  removes it from `values`, so reads return the default and no exposure
   *  fires. No-op (no notify) when the resolution is unchanged. */
  resolveForUser: (key: string, userId: string, value: unknown) => void;
  /** Forget the user resolutions of `keys` (default: every key) — the
   *  client's user no longer carries their unit's id. Values are left alone;
   *  consumers gate on `userResolved`, not on the value. */
  clearUserResolution: (keys?: string[]) => void;
  /** MV-0-017 — record the customer the Statsig user must carry (replaces
   *  any previous request). No-op on the server: the store is a process-wide
   *  singleton there. */
  requestStatsigCustomer: (request: StatsigUnitRequest) => void;
  /** MVX-371 U6 — record the person the Statsig user must carry (replaces
   *  any previous request). No-op on the server. */
  requestStatsigPerson: (request: StatsigUnitRequest) => void;
  /** MV-0-017 D5 — the customer's bounded Statsig update settled with `key`
   *  still unresolved for `userId`: mark the pair timed out (sticky). Returns
   *  whether it was newly marked — `false` when the pair is resolved, already
   *  timed out, or ever resolved (a resolved pair never times out later), and
   *  on the server (no-op there). */
  timeOutUserResolution: (key: string, userId: string) => boolean;
  /** MV-0-017 — where `key` stands for `userId`: still the server's
   *  stableID-only value (`pending`), the customer's own arm (`resolved`), or
   *  released to control by the bounded resolution (`timed-out`, sticky). */
  userResolutionState: (key: string, userId: string) => UserResolutionState;
  /** Identity change (logout / another customer): forget every user-unit
   *  resolution, timeout, release and pending Statsig customer and
   *  person —
   *  sticky decisions are per customer within a page view, never across
   *  customers — and re-arm the exposure dedup of the user-unit experiments
   *  (it is keyed per experiment, so the next customer would otherwise never
   *  be exposed). Values, readiness, gate and stableID-experiment exposures
   *  are left alone. No-op (no notify) when nothing is set; no-op on the
   *  server. MVF-368. */
  resetUserResolution: () => void;
  /** Release `key` not enrolled until the next identity reset. Returns
   *  whether it was newly released; no-op on the server. */
  releaseKey: (key: string) => boolean;
  reset: () => void;
};

export type UserResolutionState = 'pending' | 'resolved' | 'timed-out';

/** MV-0-017 — a pending `identifyCustomer` / `identifyPerson` request: the
 *  unit's id to put on the Statsig user (customerId or personId), the SDK
 *  `timeoutMs` that bounds it, and the callback for a key that is still
 *  unresolved when that update settles. */
export type StatsigUnitRequest = {
  id: string;
  timeoutMs: number;
  onTimeout?: () => void;
};

/** `exposed` Set entry for a structured exposure — internal representation
 *  (and devtools action names) keep the pre-existing namespaced strings. */
export const dedupKey = ({ kind, key }: FlagExposure): string =>
  kind === 'experiment' ? `exp:${key}` : `gate:${key}`;

const has = (o: object, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(o, key);

/** An experiment value meaning "not enrolled" — the user is outside the
 *  experiment's targeting/allocation. The server path never stores these
 *  (`providers/statsig/server.ts` keeps only non-empty values), so the key is
 *  ABSENT and `useExperiment` never fires an exposure; the client resolution
 *  mirrors that. */
const isEmptyValue = (v: unknown): boolean =>
  v == null ||
  (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0);

/** Structural equality for JSON-shaped flag values (Statsig params). */
const sameValue = (a: unknown, b: unknown): boolean => {
  if (Object.is(a, b)) return true;
  if (
    typeof a !== 'object' ||
    typeof b !== 'object' ||
    a === null ||
    b === null
  )
    return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  const ka = Object.keys(a);
  const kb = Object.keys(b);
  return (
    ka.length === kb.length &&
    ka.every(
      (k) =>
        has(b, k) &&
        sameValue(
          (a as Record<string, unknown>)[k],
          (b as Record<string, unknown>)[k]
        )
    )
  );
};

const initial = {
  values: {} as Record<string, unknown>,
  isReady: false,
  exposed: new Set<string>(),
  userResolved: {} as Record<string, string>,
  userTimedOut: {} as Record<string, string[]>,
  userEverResolved: {} as Record<string, string[]>,
  statsigCustomer: null as StatsigUnitRequest | null,
  statsigPerson: null as StatsigUnitRequest | null,
  released: {} as Record<string, true>,
};

// Browser-only writes: on the server the store is a process-wide singleton
// shared by every concurrent request.
export const isServer = () => typeof window === 'undefined';

const addTo = (
  map: Record<string, string[]>,
  key: string,
  userId: string
): Record<string, string[]> => ({
  ...map,
  [key]: [...(map[key] ?? []), userId],
});

export const _flagStore = createStore<FlagStoreState>()(
  subscribeWithSelector(
    devtools(
      (set, get) => ({
        ...initial,
        hydrate: (bootstrap) =>
          set(
            (s) => {
              // A re-hydrate (provider re-mount, navigation) must not revert a
              // user-resolved experiment to the server's stableID-only value:
              // the resolution wins — including a resolved-as-not-enrolled key,
              // which stays ABSENT even if the bootstrap carries it.
              const values = { ...bootstrap };
              for (const key of Object.keys(s.userResolved)) {
                if (has(s.values, key)) values[key] = s.values[key];
                else delete values[key];
              }
              return { values, isReady: true };
            },
            undefined,
            'hydrate'
          ),
        evaluate: async (provider, ctx) => {
          try {
            await provider.initializeClient({}, ctx);
            const values = await provider.evaluateAll(ctx);
            set({ values, isReady: true }, undefined, 'evaluate');
          } catch {
            set({ isReady: true }, undefined, 'evaluate:error');
          }
        },
        getValue: <T>(key: string, defaultValue?: T) =>
          (get().values[key] as T) ?? defaultValue,
        // On the false → true dedup transition, emits the exposure signal
        // (`_emitExposure`) that `@axo/observability` subscribes to via
        // `@axo/feature-flags/exposure` — the reversed-dependency seam
        // (MV-2251 Phase 2). Dedup no-ops never re-emit.
        markExposed: (exposure) => {
          const entry = dedupKey(exposure);
          if (get().exposed.has(entry)) return false;
          set(
            (s) => ({ exposed: new Set([...s.exposed, entry]) }),
            undefined,
            `markExposed:${entry}`
          );
          _emitExposure({ ...exposure, variant: get().values[exposure.key] });
          return true;
        },
        unmarkExposed: (exposure) => {
          const entry = dedupKey(exposure);
          set(
            (s) => {
              if (!s.exposed.has(entry)) return s;
              const next = new Set(s.exposed);
              next.delete(entry);
              return { exposed: next };
            },
            undefined,
            `unmarkExposed:${entry}`
          );
        },
        // Empty value (not enrolled) → key resolved but ABSENT from `values`.
        // Idempotent: an unchanged resolution returns the SAME state, so
        // zustand skips the notify — `setStatsigClient` re-runs the resolver
        // during render on a remount, when the reused client is already Ready
        // with a userID, and must not wake React subscribers there.
        resolveForUser: (key, userId, value) =>
          set(
            (s) => {
              const empty = isEmptyValue(value);
              const known = s.userEverResolved[key]?.includes(userId) ?? false;
              const unchanged =
                known &&
                s.userResolved[key] === userId &&
                (empty
                  ? !has(s.values, key)
                  : has(s.values, key) && sameValue(s.values[key], value));
              if (unchanged) return s;
              const values = { ...s.values };
              if (empty) delete values[key];
              else values[key] = value;
              return {
                values,
                userResolved: { ...s.userResolved, [key]: userId },
                ...(known
                  ? {}
                  : {
                      userEverResolved: addTo(s.userEverResolved, key, userId),
                    }),
              };
            },
            undefined,
            `resolveForUser:${key}`
          ),
        // Returns the SAME state when nothing is resolved so zustand skips the
        // notify: this runs from `setStatsigClient`, which client-init calls
        // during render, and a no-op write must not wake React subscribers.
        clearUserResolution: (keys) =>
          set(
            (s) => {
              const cleared = keys
                ? keys.filter((k) => has(s.userResolved, k))
                : Object.keys(s.userResolved);
              if (!cleared.length) return s;
              const userResolved = { ...s.userResolved };
              cleared.forEach((k) => delete userResolved[k]);
              return { userResolved };
            },
            undefined,
            'clearUserResolution'
          ),
        requestStatsigCustomer: (request) => {
          if (isServer()) return;
          set(
            { statsigCustomer: request },
            undefined,
            'requestStatsigCustomer'
          );
        },
        requestStatsigPerson: (request) => {
          if (isServer()) return;
          set({ statsigPerson: request }, undefined, 'requestStatsigPerson');
        },
        timeOutUserResolution: (key, userId) => {
          if (isServer()) return false;
          const s = get();
          if (s.userEverResolved[key]?.includes(userId)) return false;
          if (s.userResolutionState(key, userId) !== 'pending') return false;
          set(
            (st) => ({ userTimedOut: addTo(st.userTimedOut, key, userId) }),
            undefined,
            `userResolutionTimedOut:${key}`
          );
          return true;
        },
        userResolutionState: (key, userId) => {
          const s = get();
          if (s.userTimedOut[key]?.includes(userId)) return 'timed-out';
          return s.userResolved[key] === userId ? 'resolved' : 'pending';
        },
        resetUserResolution: () => {
          if (isServer()) return;
          const s = get();
          const userUnitExposed = userUnitExperimentKeys()
            .map((key) => dedupKey({ kind: 'experiment', key }))
            .filter((entry) => s.exposed.has(entry));
          const empty =
            !Object.keys(s.userResolved).length &&
            !Object.keys(s.userTimedOut).length &&
            !Object.keys(s.userEverResolved).length &&
            s.statsigCustomer === null &&
            s.statsigPerson === null &&
            !Object.keys(s.released).length &&
            !userUnitExposed.length;
          if (empty) return;
          const exposed = new Set(s.exposed);
          userUnitExposed.forEach((entry) => exposed.delete(entry));
          set(
            {
              userResolved: {},
              userTimedOut: {},
              userEverResolved: {},
              statsigCustomer: null,
              statsigPerson: null,
              released: {},
              exposed,
            },
            undefined,
            'resetUserResolution'
          );
        },
        releaseKey: (key) => {
          if (isServer() || get().released[key]) return false;
          set(
            (s) => ({ released: { ...s.released, [key]: true } }),
            undefined,
            `releaseKey:${key}`
          );
          return true;
        },
        reset: () =>
          set(
            {
              values: {},
              isReady: false,
              exposed: new Set<string>(),
              userResolved: {},
              userTimedOut: {},
              userEverResolved: {},
              statsigCustomer: null,
              statsigPerson: null,
              released: {},
            },
            undefined,
            'reset'
          ),
      }),
      { name: '@axo/feature-flags' }
    )
  )
);
